import { eachDay, isoWeekday, monthOf, weekOf } from '../calendar/dates.js';
import { type HolidayRegion, holidayOn } from '../calendar/holidays.js';

// Capacity of a person per day (RES-02, RES-05, docs/03 section 5):
//   available = (profile hours (weekday, valid profile) − public holiday − absence (full/half))
//               × bookable share
//   booked    = Σ allocations on days the person works
//               (hours per day, or percent × profile hours × bookable share)
// The bookable share (RES-13) is the part of the working time that can be planned in projects;
// a percent booking refers to it, so 100 % means fully planned.
// Bookings still count on absence days, so the heatmap shows the conflict as overbooking; on
// holidays and days without profile hours (weekend, part-time day off) nothing is booked.

export const weekdays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof weekdays)[number];
export type WeeklyHours = Record<Weekday, number>;

/** Applies to everyone without a work profile, and before a person's first profile. */
export const defaultWeeklyHours: WeeklyHours = {
  mon: 8,
  tue: 8,
  wed: 8,
  thu: 8,
  fri: 8,
  sat: 0,
  sun: 0,
};

/** Company default of the bookable share (RES-13) until an administrator changes it. */
export const DEFAULT_BOOKABLE_PERCENT = 80;

export interface WorkProfileInput {
  /** Valid from this date until the next profile's `validFrom`. */
  validFrom: string;
  hours: WeeklyHours;
}

export type HalfDay = 'none' | 'first_half' | 'second_half';

export interface AbsenceInput {
  startDate: string;
  endDate: string;
  /** Only meaningful for one-day absences. */
  halfDay: HalfDay;
}

export interface CapacityInput {
  region: HolidayRegion;
  profiles: readonly WorkProfileInput[];
  absences: readonly AbsenceInput[];
  /** Part of the working time that can be booked, 0–100 (RES-13); 100 without. */
  bookablePercent?: number;
}

export type AllocationUnit = 'hours_per_day' | 'percent';

export interface AllocationInput {
  startDate: string;
  endDate: string;
  unit: AllocationUnit;
  amount: number;
}

export interface DayCapacity {
  date: string;
  /** Hours of the valid work profile on this weekday, before holidays and absences. */
  profileHours: number;
  holiday: boolean;
  absence: 'none' | 'half' | 'full';
  /** Bookable share of the working time, 0–100 (RES-13). */
  bookablePercent: number;
  /** Bookable hours: working time after holidays and absences × bookable share. */
  available: number;
}

/** Hours to two decimals, like `numeric(…, 2)` in the database. */
function round(hours: number): number {
  return Math.round(hours * 100) / 100;
}

/** Profile valid on `date`: the one with the latest `validFrom` not after `date`. */
export function profileOn(date: string, profiles: readonly WorkProfileInput[]): WeeklyHours {
  let valid: WorkProfileInput | undefined;
  for (const profile of profiles) {
    if (profile.validFrom <= date && (!valid || profile.validFrom > valid.validFrom)) {
      valid = profile;
    }
  }
  return valid?.hours ?? defaultWeeklyHours;
}

/** Weekly working time of a profile and the part of it that can be booked (RES-13). */
export function weeklyHours(
  hours: WeeklyHours,
  bookablePercent: number,
): { total: number; bookable: number } {
  const total = round(weekdays.reduce((sum, day) => sum + hours[day], 0));
  return { total, bookable: round((total * bookablePercent) / 100) };
}

export function profileHoursOn(date: string, profiles: readonly WorkProfileInput[]): number {
  // isoWeekday is 1–7, so the index is always within the tuple.
  const weekday = weekdays[isoWeekday(date) - 1] as Weekday;
  return profileOn(date, profiles)[weekday];
}

function absenceOn(date: string, absences: readonly AbsenceInput[]): DayCapacity['absence'] {
  let result: DayCapacity['absence'] = 'none';
  for (const absence of absences) {
    if (absence.startDate > date || absence.endDate < date) continue;
    const half = absence.halfDay !== 'none' && absence.startDate === absence.endDate;
    if (!half) return 'full';
    result = 'half';
  }
  return result;
}

export function capacityOn(date: string, input: CapacityInput): DayCapacity {
  const profileHours = profileHoursOn(date, input.profiles);
  const holiday = holidayOn(date, input.region) !== null;
  const absence = absenceOn(date, input.absences);
  const bookablePercent = input.bookablePercent ?? 100;
  let working = profileHours;
  if (holiday || absence === 'full') working = 0;
  else if (absence === 'half') working = profileHours / 2;
  return {
    date,
    profileHours,
    holiday,
    absence,
    bookablePercent,
    available: round((working * bookablePercent) / 100),
  };
}

/** Capacity for every day from `from` to `to` (inclusive). */
export function dailyCapacity(from: string, to: string, input: CapacityInput): DayCapacity[] {
  return eachDay(from, to).map((date) => capacityOn(date, input));
}

/** Hours an allocation books on one day of the person (0 outside its range). */
export function bookedHoursOn(allocation: AllocationInput, day: DayCapacity): number {
  if (day.date < allocation.startDate || day.date > allocation.endDate) return 0;
  if (day.holiday || day.profileHours === 0) return 0;
  const hours =
    allocation.unit === 'hours_per_day'
      ? allocation.amount
      : (allocation.amount / 100) * day.profileHours * (day.bookablePercent / 100);
  return round(hours);
}

/** Booked hours per day, summed over all allocations; same order as `days`. */
export function dailyBookedHours(
  allocations: readonly AllocationInput[],
  days: readonly DayCapacity[],
): number[] {
  return days.map((day) =>
    round(allocations.reduce((sum, allocation) => sum + bookedHoursOn(allocation, day), 0)),
  );
}

// --- Aggregation (heatmap columns) ------------------------------------------------------------

export type Granularity = 'day' | 'week' | 'month';

/** First day of the bucket containing `date`: the day itself, its Monday or the 1st. */
export function bucketStart(date: string, granularity: Granularity): string {
  if (granularity === 'week') return weekOf(date).from;
  if (granularity === 'month') return monthOf(date).from;
  return date;
}

export interface DayValues {
  date: string;
  available: number;
  booked: number;
}

export interface Bucket {
  /** Bucket key: first day of the day/week/month, also if it lies before the range. */
  start: string;
  /** First and last day of the bucket within the aggregated days. */
  from: string;
  to: string;
  available: number;
  booked: number;
}

/** Sums daily values into day, week or month buckets, in date order. */
export function aggregate(days: readonly DayValues[], granularity: Granularity): Bucket[] {
  const buckets: Bucket[] = [];
  let current: Bucket | undefined;
  for (const day of [...days].sort((a, b) => a.date.localeCompare(b.date))) {
    const start = bucketStart(day.date, granularity);
    if (current?.start !== start) {
      current = { start, from: day.date, to: day.date, available: 0, booked: 0 };
      buckets.push(current);
    }
    current.to = day.date;
    current.available = round(current.available + day.available);
    current.booked = round(current.booked + day.booked);
  }
  return buckets;
}
